import { Maybe, ProductEdge } from "@/shared/services/graphql/generated/admin-graph-types";
import { ProductSortFields, SortOrder } from "@/shared/services/graphql/generated/consumer-graph-types";
import { ComponentDefinition } from "@/shared/components/component-definition.model";
import { LazyLoadingDataSource } from "@/shared/components/scroll/lazy-loading-datasource";
import { computed, ComputedRef, ref, Ref } from "vue";
import { advProduct } from "../data-objects/advanced-product";
import ProductResponsiveCard from "./components/product-selection/product-responsive-card.vue";
import { useHasAccess } from "@/shared/access-control/composables/use-has-access";
import { useI18n } from "vue-i18n";
import { AccessAction, AccessResource } from "@/shared/access-control/access-control";
import { MenuEntry, MenuTextEntry } from "@/shared/components/menu/menu.model";
import { router } from "@/app/router.ts";
import { useScreenSize } from "@/shared/screen/composables/screen-size.ts";
import ShareMenuEntry from "@/shared/share/share-menu-entry.vue";
import { SortEntry } from "@/shared/components/sort-dropdown/sort-dropdown.model";

//TODO move functions and composables in separate files
export function createDataSource(
    emitSelection: boolean,
    showButtons: boolean
): LazyLoadingDataSource<Maybe<ProductEdge>, ComponentDefinition> {
    return new LazyLoadingDataSource<Maybe<ProductEdge>, ComponentDefinition>((item) => {
        return item
            ? {
                  getComponent: () => ProductResponsiveCard,
                  props: { product: advProduct(item.node), showButtons: showButtons, emitSelection: emitSelection },
              }
            : undefined;
    });
}

export function createTiles(
    dataSource: LazyLoadingDataSource<Maybe<ProductEdge>, ComponentDefinition>,
    products: Array<Maybe<ProductEdge>>
): Array<ComponentDefinition> {
    dataSource.setSourceValues(products ?? []);
    return dataSource.getResults();
}

export function useProductsOverviewSort(isSearch: boolean) {
    const { t } = useI18n();

    const sorting: Ref<SortEntry> = ref(
        isSearch
            ? { field: ProductSortFields.score, order: SortOrder.desc }
            : { field: ProductSortFields.title, order: SortOrder.asc }
    );

    const sortOrderItems = computed(() => {
        return [
            ...(isSearch
                ? [
                      {
                          id: "Ranking",
                          title: t("shared.Ranking"),
                          value: {
                              field: ProductSortFields.score,
                              order: SortOrder.desc,
                          },
                      },
                  ]
                : []),
            {
                id: "ID ascending",
                title: t("product-and-asset.ID ascending"),
                value: {
                    field: ProductSortFields.id,
                    order: SortOrder.asc,
                },
            },
            {
                id: "ID descending",
                title: t("product-and-asset.ID descending"),
                value: {
                    field: ProductSortFields.id,
                    order: SortOrder.desc,
                },
            },
            {
                id: "Title ascending",
                title: t("product-and-asset.Title ascending"),
                value: {
                    field: ProductSortFields.title,
                    order: SortOrder.asc,
                },
            },
            {
                id: "Title descending",
                title: t("product-and-asset.Title descending"),
                value: {
                    field: ProductSortFields.title,
                    order: SortOrder.desc,
                },
            },
        ];
    });

    return {
        sortOrderItems,
        sorting,
    };
}

export function useProductsOverviewConfig() {
    const { t } = useI18n();
    const { isXSmall } = useScreenSize();

    const { hasAccess: hasConfigAccess } = useHasAccess({
        resource: AccessResource.data_display_configs,
        action: AccessAction.write,
    });

    const { hasAccess: hasFilterConfigAccess } = useHasAccess({
        resource: AccessResource.facet_collections,
        action: AccessAction.write,
    });

    const configMenuEntries: ComputedRef<(MenuTextEntry | MenuEntry)[]> = computed(() => {
        const entries: (MenuTextEntry | MenuEntry)[] = [
            {
                id: "configure-view",
                text: t("shared.Configure view"),
                icon: "fa-regular fa-sliders-simple icon-color",
                click: () => {
                    const route = router.resolve({ path: "/admin/datadisplay/product-selection" });
                    window.open(route.href, "_blank");
                },
                visible: hasConfigAccess.value,
                elementAttrs: { "data-qs": "product-settings-toolbar-configure-button" },
            },
            {
                id: "configure-filter",
                text: t("shared.Configure filter"),
                icon: "fa-regular fa-sliders-simple icon-color",
                click: () => {
                    const route = router.resolve({ path: "/admin/filter/product-selection" });
                    window.open(route.href, "_blank");
                },
                visible: hasFilterConfigAccess.value,
                elementAttrs: { "data-qs": "product-settings-toolbar-configure-filter-button" },
            },
        ];

        if (isXSmall.value) {
            entries.push({
                id: "share-entry",
                component: {
                    getComponent: () => ShareMenuEntry,
                },
            });
        }

        return entries;
    });

    return {
        configMenuEntries,
    };
}
